<!-- Template -->
<template>
  <div class="gateway-add-endpoint">
    <!-- Add Endpoint -->
    <div>
      <hr style="margin: 0;">
      <p style="margin: 15px; margin-top: 30px;">
        In this form you are adding anew endpoint to the selected gateway in the CareAI ecosystem. Upon success, you can continue adding routes or deploy this new revision.
      </p>
      <form style="padding: 15px;">
        <div class="form-group">
          <label for="name">Name</label>
          <input v-model="name" v-on:change="validate(name_validator, name)" type="text" class="form-control" id="name" aria-describedby="name" placeholder="Name">
          <small id="name" v-bind:class="{ 'gateway__target_form--invalid': !name_validator.valid }" class="form-text text-muted">{{ name_validator.message }}</small>
        </div>
        <div class="form-group">
          <label for="hostname">Hostname</label>
          <select v-on:change="validate(host_name_validator, hostname)" v-model="hostname" class="form-select form-select-sm" aria-label="Select host">
            <option selected value="1">Hostname</option>
            <option v-for="server in targetServers" :value="server.id" :key="server.id">{{ server.host }}</option>
          </select>
          <small class="form-text text-muted" v-bind:class="{ 'gateway__target_form--invalid': !host_name_validator.valid }">{{ host_name_validator.message }}</small>
        </div>
        <div class="form-group">
          <label for="method">Method</label>
          <select v-model="method" v-on:change="validate(method_validator, method)" class="form-select form-select-sm" aria-label="Select method">
            <option selected value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="POST">PATCH</option>
            <option value="POST">PUT</option>
            <option value="POST">DELETE</option>
          </select>
          <small class="form-text text-muted">Select a valid method.</small>
        </div>
        <div class="form-group">
          <label for="endpoint">Endpoint</label>
          <input v-model="endpoint" v-on:change="validate(endpoint_validator, endpoint)" @input="formatEndpoint(endpoint)"  type="text" class="form-control" id="end_point" aria-describedby="endpoint" placeholder="Endpoint">
          <small id="endpoint" v-bind:class="{ 'gateway__target_form--invalid': !endpoint_validator.valid }" class="form-text text-muted">{{ endpoint_validator.message }}</small>
        </div>
        <div class="form-group">
          <label for="quota">Quota</label>
          <select v-on:change="validate(quota_validator, quota)" v-model="quota" class="form-select form-select-sm" aria-label="Select quota">
            <option selected value="1">Quota policy</option>
            <option value="FC-50CPM-Quota">50CPM</option>
            <option value="FC-5000CPM-Quota">5000CPM</option>
          </select>
          <small v-bind:class="{ 'gateway__target_form--invalid': !quota_validator.valid }" class="form-text text-muted">Set a valid configured quota policy.</small>
        </div>
        <button :disabled="!endpoint_validator.valid || !host_name_validator.valid || !quota_validator.valid || !name_validator.valid || !method_validator.valid" type="submit" @click.prevent="submit(name, method, hostname, endpoint, quota)" class="btn btn-primary gateway__tool-button">Submit</button>
      </form>
    </div>
  </div>
</template>
<!-- End Template -->

<!-- Scripts -->
<script>
import Vue from "vue";
import VueToast from "vue-toast-notification";
import "vue-toast-notification/dist/theme-sugar.css";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import Dropdown from "vue-simple-search-dropdown";

Vue.use(VueToast);
Vue.use(Loading);

export default {
  name: "GatewayAddEndpoint",
  components: { Dropdown },
  data() {
    return {
      // View State & Init
      loading: true,
      viewReady: false,

      // Form Validators
      endpoint: "",
      endpoint_validator: { 
        valid: true, 
        message: "Enter a unique endpoint for this gateway in the following format. You should note the path parameters being wrapped in curly brackets. IE /section/{one}/subsection/{two} ",
        validator: (val) => {
          this.endpoint_validator.message = "Enter a unique endpoint for this gateway in the following format. You should note the path parameters being wrapped in curly brackets. IE /section/{one}/subsection/{two}";
          if (!val) {
            this.endpoint_validator.message = "Cannot be empty. IE /section/{one}/subsection/{two}";
            this.endpoint_validator.valid = false;
          } else if (val.search(/^(?:\/(?:[a-z0-9]+(?:[A-Z\-][a-z0-9]+)*|{[a-z0-9]+(?:[A-Z\-][a-z0-9]+)*}))+$/) === -1) {
            this.endpoint_validator.message = "Only curly brackets, dashes, forward slashes and alphanumerics are supported. You should note the path parameters being wrapped in curly brackets. Should not end with a forward slash. IE /section/{one}/subsection/{two}";
            this.endpoint_validator.valid = true;
          } else if (this.revision.flows.find((flow) => {
            let found = false;

            // If exact match
            if (flow.pattern === val) found = true;

            // If matches, but variable name changes 
            
            const flowFormat = flow.pattern.replace(/\s*\{.*?\}\s*/g, "");
            const valFormat = val.replace(/\s*\{.*?\}\s*/g, "");
            if (flowFormat === valFormat) found = true;

            return false;
          })) {
            this.endpoint_validator.message = "This pattern already exists. Try another endpoint.";
            this.endpoint_validator.valid = false;
          }
          else this.endpoint_validator.valid = true;
        }
      },
      quota_validator: { 
        valid: true, 
        message: "Select an available quota policy.",
        validator: (val) => {
          this.quota_validator.message = "Select an available quota policy.";
          if (Number(val) === 1 || !val) {
            this.quota_validator.message = "Select one of the available options.";
            this.quota_validator.valid = false;
          } 
          else this.quota_validator.valid = true;
        }
      },
      name: "",
      name_validator: { 
        valid: true, 
        message: "Set a name. Minimum 7 characters. Must be unique. Example: infotainmentservice.endcall or virtualvisit.getallevents",
        validator: (val) => {
          this.name_validator.message = "Set a name. Minimum 7 characters. Must be unique. Example: infotainmentservice.endcall or virtualvisit.getallevents";
          if (!val || val.length < 7) {
            this.name_validator.message = "Set a name. Minimum 7 characters. Must be unique. Example: infotainmentservice.endcall or virtualvisit.getallevents";
            this.name_validator.valid = false;
          } else if (this.revision && Array.isArray(this.revision.flows) && this.revision.flows.find((flow) => {
            let found = false;

            // Must not be a prexisting name
            if (flow.name.trim() === val.trim()) found = true;

            return found;
          })) {
            this.name_validator.message = "This name already exists. Try another endpoint.";
            this.name_validator.valid = false;
          }
          else this.name_validator.valid = true;
          console.log(this.revision);
        }
      },
      host_name_validator: { 
        valid: true, 
        message: "You must select a host for the gateway to forward to. You can add a new host in the target server list.",
        validator: (val) => {
          this.host_name_validator.message  = "You must select a host for the gateway to forward to. You can add a new host in the target server list.";
          if (Number(val) === 1 || !val) {
            this.host_name_validator.message = "Select one of the available options.";
            this.host_name_validator.valid = false;
          } 
          else this.host_name_validator.valid = true;
        }
      },
      method_validator: { 
        valid: true, 
        message: "Choose a method from the list.",
        validator: (val) => {
          this.host_name_validator.message  = "Choose a method from the list.";
          if (!['GET', 'PUT', 'PATCH', 'DELETE', 'POST'].includes(val)) {
            this.host_name_validator.message = "Select one of the available options.";
            this.host_name_validator.valid = false;
          } 
          else this.host_name_validator.valid = true;
        }
      }
    };
  },
  props: ["revision", "environment", "targetServers"],

  created() {
    this.onInit();
  },

  methods: {
    async onInit() {
      if (!this.$auth.isAuthenticated) {
        return;
      }
      console.log(this.revision, this.environment, this.targetServers);
    },

    async submit(name, method, host, endpoint, quota) {
      console.log(name, method, host, endpoint, quota);
      console.log('revision', this.revision);
      console.log('targetServers', this.targetServers);
      this.validate(this.name_validator, name); 
      this.validate(this.host_name_validator, host); 
      this.validate(this.endpoint_validator, endpoint); 
      this.validate(this.quota_validator, quota); 
      if (this.endpoint_validator.valid && this.host_name_validator.valid && this.quota_validator.valid) {        
        const claims = await this.$auth.getIdTokenClaims();
        const id_token = claims.__raw;
        var request = new Request(
          config.OPERATIONS_SERVICE + `/environments/${this.environment.id}/apis/${this.revision.gatewayId}/routes`, 
          {
            method: 'PATCH',
            mode: 'cors',
            cache: 'default',
            withCredentials: true,
            credentials: 'include',
            headers: {
              Authorization: 'Bearer ' + id_token,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name, method, host, endpoint, quota, revision: this.revision.gatewayRevision
            })
          }
        );

        // Attempt to get Gateways
        try {
          const response = await fetch(request);
          if (response.status === 403) {
            Vue.$toast.open({
              message: "Uh oh... You don't have the permission required to access this resource",
              type: 'error',
            });
            return;
          }
          if (response.status == 401) {
            Vue.$toast.open({
              message: 'Your session has expired please login again to continue',
              type: 'error',
            });
            return;
          }
          if (response.status == 503) {
            return this.submitTargetServer(name, host);
          }
          if (response.status != 200) {
            Vue.$toast.open({
              message: 'Unable to get data',
              type: 'error',
            });
            return;
          } else {
            // Post
            await response.json();
            this.$emit('refresh-gateway', true);
          }
        } catch (tex) {
          Vue.$toast.open({message: 'Failed to create target due to an error.', type: 'error'});
        }
      }
    },

    formatEndpoint(val) {
      if (!val.startsWith('/')) val = `/${val}`;
      this.endpoint = val;
    },

    validate(validator, value) {
      validator.validator(value);
    },
  },
  watch: {},
};
</script>
<!-- End Scripts -->

<!-- Styles -->
<style lang="scss" scoped>
.gateway-add-endpoint {
  select {
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
  }

  a {
    color: rgb(37, 169, 202);
    display: flex;
    align-items: center;
    margin-top: 15px;
    text-decoration: none;
    transition: color 500ms;

    &:hover {
      color: rgb(24, 194, 236);
    }
  }

  label {
    color: black;
    font-weight: 600;
  }

  .gateway__method-bubble {
    text-decoration: none;
    color: white;
    padding: 5px;
    background-color: #222222;
    border-radius: 2px;
  }

  .gateway__target_form--invalid {
    color: #F27778 !important;
  }
  
  .gateway__get-bubble {
    background-color: #66BE6D;
  }

  .gateway__delete-bubble {
    background-color: #F27778;
  }

  .gateway__patch-bubble {
    background-color: #eaf7ff;
  }

  .gateway__post-bubble {
    background-color: #484848;
  }

  .gateway__put-bubble {
    background-color: #1dd8f6;
  }

  .btn-primary.disabled, .btn-primary:disabled {
    background-color: #484848;
  }
  .loader {
    border: 3px solid #f3f3f3; /* Light grey */
    border-top: 3px solid rgba(37, 168, 202, 0.8); /* Blue */
    border-radius: 50%;
    width: 45px;
    height: 45px;
    animation: spin 1.5s linear infinite;
  }

  .accordion-heading {
    position: relative;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .collapse.in {
    overflow: visible;
  }

  .ml-a {
    margin-left: auto;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
<!-- End Styles -->